// Reads the discount data the POS order details API returns for orders placed from the sell screen:
// - orderedProductDiscountsMappings: one row per applied discount per line (discountDetails.type set by posOrderDiscount.service)
// - orders.discountAmount: total discount (item + coupon + additional), excluding free items
// - additionalDiscountAmount: cashier's ad-hoc discount
// - ordered product appliedDiscountValue: total discount on the line; taxAmount: tax per unit

export type OrderDiscountMapping = {
	discountId: number
	discountValue: string | number | null
	orderedProductId: number | null
	discountDetails: { type?: string, title?: string | null, promoCode?: string | null } | null
}

export type LineDiscountLabel = { discountId: number, type: string, title: string, amount: number }

export type OrderDiscountSummary = {
	// false for older orders: only the total is known
	hasBreakdown: boolean
	itemDiscount: number
	couponDiscount: number
	couponCodes: string[]
	additionalDiscount: number
	freeItemsValue: number
	totalDiscount: number
	totalSavings: number
	linesByOrderedProductId: Record<number, LineDiscountLabel[]>
}

const round2 = (value: number) => Math.round(value * 100) / 100
const toNumber = (value: unknown) => {
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : 0
}

export const summariseOrderDiscounts = (order: {
	discountAmount?: string | number | null
	additionalDiscountAmount?: string | number | null
	orderedProductDiscountsMappings?: OrderDiscountMapping[] | null
} | null | undefined): OrderDiscountSummary => {
	const mappings = (order?.orderedProductDiscountsMappings ?? []).filter((mapping) => mapping?.discountDetails?.type)
	const sumOf = (types: string[]) => round2(mappings
		.filter((mapping) => types.includes(mapping.discountDetails!.type!))
		.reduce((sum, mapping) => sum + toNumber(mapping.discountValue), 0))

	const linesByOrderedProductId: Record<number, LineDiscountLabel[]> = {}
	mappings.forEach((mapping) => {
		if (mapping.orderedProductId == null) return
		const type = mapping.discountDetails!.type!
		const title = type === "COUPON"
			? `Coupon ${mapping.discountDetails?.promoCode ?? ""}`.trim()
			: mapping.discountDetails?.title || "Discount"
		;(linesByOrderedProductId[mapping.orderedProductId] ??= []).push({ discountId: mapping.discountId, type, title, amount: round2(toNumber(mapping.discountValue)) })
	})

	const totalDiscount = round2(toNumber(order?.discountAmount))
	const freeItemsValue = sumOf(["PAY_X_GET_FREE"])
	return {
		hasBreakdown: mappings.length > 0,
		itemDiscount: sumOf(["AUTOMATIC", "BUY_X_GET_Y"]),
		couponDiscount: sumOf(["COUPON"]),
		couponCodes: [...new Set(mappings.filter((mapping) => mapping.discountDetails?.type === "COUPON" && mapping.discountDetails?.promoCode).map((mapping) => mapping.discountDetails!.promoCode!))],
		additionalDiscount: round2(toNumber(order?.additionalDiscountAmount)),
		freeItemsValue,
		totalDiscount,
		totalSavings: round2(totalDiscount + freeItemsValue),
		linesByOrderedProductId,
	}
}

// Round off is not stored: the order keeps the exact total and the POS payment records the rounded amount
// collected, so the round off is their difference. Rounding to the nearest ₹1 never moves more than 0.50,
// so anything larger (or a missing payment) is not treated as a round off.
export const orderRoundOff = (order: {
	totalAmount?: string | number | null
	paymentOrders?: { totalAmount?: string | number | null }[] | null
} | null | undefined): { roundOff: number, payableTotal: number } => {
	const exact = round2(toNumber(order?.totalAmount))
	const paymentTotal = order?.paymentOrders?.[0]?.totalAmount
	if (paymentTotal == null || paymentTotal === "") return { roundOff: 0, payableTotal: exact }
	const roundOff = round2(toNumber(paymentTotal) - exact)
	if (roundOff === 0 || Math.abs(roundOff) > 0.5) return { roundOff: 0, payableTotal: exact }
	return { roundOff, payableTotal: round2(exact + roundOff) }
}

// Amount the customer paid for a line, tax included (taxAmount is per unit, appliedDiscountValue is for the line)
export const orderLineTotal = (product: { productPrice?: string | number | null, quantity?: string | number | null, taxAmount?: string | number | null, appliedDiscountValue?: string | number | null }) => {
	const quantity = toNumber(product?.quantity)
	return round2(toNumber(product?.productPrice) * quantity - toNumber(product?.appliedDiscountValue) + toNumber(product?.taxAmount) * quantity)
}

// Discount to take back when returning `returnQty` units. Uses the line's own discount when the order
// recorded one; older orders fall back to spreading the order discount evenly across the subtotal.
export const returnDiscountForProduct = ({ product, returnQty, unitPrice, orderDiscount, orderSubTotal }: {
	product?: { quantity?: string | number | null, appliedDiscountValue?: string | number | null } | null
	returnQty: number
	unitPrice: number
	orderDiscount: string | number | null | undefined
	orderSubTotal: string | number | null | undefined
}) => {
	const lineQuantity = toNumber(product?.quantity)
	const lineDiscount = toNumber(product?.appliedDiscountValue)
	if (product && lineDiscount > 0 && lineQuantity > 0) {
		return round2((lineDiscount / lineQuantity) * returnQty)
	}
	const subTotal = toNumber(orderSubTotal)
	if (!subTotal) return 0
	return round2(unitPrice * returnQty * (toNumber(orderDiscount) / subTotal))
}
